/**
 * CTR PDP Main — gallery, variant selector, modals, ATC, and product switching.
 * Adapted from lp-product-buy-box inline script.
 */

import { morph } from '@theme/morph';
import { initFactsPopups, initOfferCalloutModal, refreshFactsPopupModals } from '@theme/ctr-facts-popup';
import { initSubscriptionFreeGiftCleanup, initSubscriptionFreeGiftForms } from '@theme/subscription-free-gift';

/** @type {WeakMap<HTMLElement, AbortController>} */
const productSwitchControllers = new WeakMap();

/**
 * @param {string} sectionId
 * @returns {string}
 */
function normalizeSectionId(sectionId) {
  return sectionId.replace(/^shopify-section-/, '');
}

/**
 * @param {string} html
 * @param {string} sectionId
 * @returns {HTMLElement | null}
 */
function extractSwappableFromHtml(html, sectionId) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const normalizedId = normalizeSectionId(sectionId);
  const sectionRoot =
    doc.querySelector(`#shopify-section-${normalizedId} .ctr-pdp-main`) ||
    doc.querySelector('.ctr-pdp-main');

  if (!(sectionRoot instanceof HTMLElement)) return null;

  const wrappedSwappable = sectionRoot.querySelector(
    '[data-ctr-pdp-swappable]:has(.ctr-pdp-main__content-column)'
  );
  if (wrappedSwappable instanceof HTMLElement) return wrappedSwappable;

  const layout = sectionRoot.querySelector('.ctr-pdp-main__layout');
  if (layout instanceof HTMLElement) return layout;

  return sectionRoot;
}

/**
 * @param {string} productUrl
 * @param {string} templateView
 * @param {string} sectionId
 * @param {AbortSignal} signal
 * @returns {Promise<{ html: string }>}
 */
async function fetchProductSectionHtml(productUrl, templateView, sectionId, signal) {
  const baseUrl = new URL(productUrl, window.location.origin);
  if (templateView) {
    baseUrl.searchParams.set('view', templateView);
  }

  const normalizedSectionId = normalizeSectionId(sectionId);

  const sectionsUrl = new URL(baseUrl);
  sectionsUrl.searchParams.set('sections', normalizedSectionId);

  const sectionsResponse = await fetch(sectionsUrl.toString(), { signal });
  if (sectionsResponse.ok) {
    try {
      const sectionsJson = await sectionsResponse.json();
      const sectionHtml = sectionsJson[normalizedSectionId];
      if (typeof sectionHtml === 'string' && sectionHtml.trim()) {
        return { html: sectionHtml };
      }
    } catch (error) {
      // Fall through to section_id request.
    }
  }

  const sectionUrl = new URL(baseUrl);
  sectionUrl.searchParams.set('section_id', normalizedSectionId);

  const sectionResponse = await fetch(sectionUrl.toString(), { signal });
  if (!sectionResponse.ok) {
    throw new Error(`Product section fetch failed with status ${sectionResponse.status}`);
  }

  const sectionHtml = await sectionResponse.text();
  if (!sectionHtml.trim()) {
    throw new Error('Product section fetch returned empty HTML');
  }

  return { html: sectionHtml };
}

/**
 * @param {HTMLElement} section
 * @param {string} html
 */
function applySectionAppearance(section, html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const newSection = doc.querySelector('.ctr-pdp-main');

  if (!(newSection instanceof HTMLElement) || !newSection.style.cssText) return;

  section.style.cssText = newSection.style.cssText;
}

/**
 * @param {ParentNode} root
 * @returns {{ gallery: HTMLElement | null, above: HTMLElement | null, buybox: HTMLElement | null }}
 */
function extractSwappableParts(root) {
  return {
    gallery: root.querySelector('.ctr-pdp-main__gallery'),
    above: root.querySelector('.ctr-pdp-main__content-above-buybox'),
    buybox: root.querySelector('.ctr-pdp-main__content-buybox'),
  };
}

/**
 * @param {HTMLElement} section
 * @returns {boolean}
 */
function sectionHasProductSelector(section) {
  return section.classList.contains('ctr-pdp-main--has-product-selector');
}

/**
 * @param {HTMLElement} section
 * @returns {HTMLElement[]}
 */
function getProductSwitchLoadingTargets(section) {
  if (sectionHasProductSelector(section)) {
    return [
      section.querySelector('[data-ctr-pdp-swappable="gallery"]'),
      section.querySelector('.ctr-pdp-main__layout--has-product-selector .ctr-pdp-main__content-column'),
    ].filter((element) => element instanceof HTMLElement);
  }

  const swappable = section.querySelector('[data-ctr-pdp-swappable]');
  return swappable instanceof HTMLElement ? [swappable] : [];
}

function extractSectionRoot(html, sectionId) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const normalizedId = normalizeSectionId(sectionId);

  return (
    doc.querySelector(`#shopify-section-${normalizedId} .ctr-pdp-main`) ||
    doc.querySelector('.ctr-pdp-main')
  );
}

/**
 * @param {HTMLElement} section
 * @param {ParentNode} fetchedSwappable
 * @param {ParentNode | null | undefined} fetchedSectionRoot
 */
function applySplitSwappableContents(section, fetchedSwappable, fetchedSectionRoot) {
  const fetchedParts = extractSwappableParts(fetchedSwappable);
  const targets = {
    gallery: section.querySelector('[data-ctr-pdp-swappable="gallery"]'),
    above: section.querySelector('[data-ctr-pdp-swappable="above"]'),
    buybox: section.querySelector('[data-ctr-pdp-swappable="buybox"]'),
    modals: section.querySelector('[data-ctr-pdp-swappable="modals"]'),
  };

  for (const key of ['gallery', 'above', 'buybox']) {
    const target = targets[key];
    const source = fetchedParts[key];
    if (target instanceof HTMLElement && source instanceof HTMLElement) {
      morph(target, source);
    }
  }

  const modalsSource = fetchedSectionRoot?.querySelector('[data-ctr-pdp-swappable="modals"]');
  if (targets.modals instanceof HTMLElement && modalsSource instanceof HTMLElement) {
    morph(targets.modals, modalsSource);
    refreshFactsPopupModals(section);
  }
}

/**
 * @param {HTMLElement} swappable
 * @param {HTMLElement} newSwappable
 */
function replaceSwappableContents(swappable, newSwappable) {
  morph(swappable, newSwappable);
}

/**
 * @param {HTMLElement} section
 * @param {HTMLInputElement} input
 */
async function switchProductFormula(section, input) {
  const productUrl = input.dataset.productUrl;
  const templateView = input.dataset.templateView || '';
  const sectionId = input.dataset.sraSectionId;
  const hasProductSelector = sectionHasProductSelector(section);
  const loadingTargets = getProductSwitchLoadingTargets(section);

  if (!productUrl || !sectionId || (!hasProductSelector && loadingTargets.length === 0)) {
    if (!sectionId) {
      console.warn('CTR PDP product switch failed: missing data-sra-section-id on option');
    }
    return;
  }

  productSwitchControllers.get(section)?.abort();
  const abortController = new AbortController();
  productSwitchControllers.set(section, abortController);

  loadingTargets.forEach((target) => {
    target.setAttribute('aria-busy', 'true');
    target.classList.add('is-loading');
  });

  try {
    const { html } = await fetchProductSectionHtml(
      productUrl,
      templateView,
      sectionId,
      abortController.signal
    );

    const fetchedSectionRoot = extractSectionRoot(html, sectionId);
    const newSwappable = extractSwappableFromHtml(html, sectionId);
    if (!newSwappable) {
      throw new Error('No swappable region found in Section Rendering response');
    }

    applySectionAppearance(section, html);

    if (hasProductSelector) {
      applySplitSwappableContents(section, newSwappable, fetchedSectionRoot);
    } else {
      const swappable = section.querySelector('[data-ctr-pdp-swappable]');
      if (swappable instanceof HTMLElement) {
        replaceSwappableContents(swappable, newSwappable);
      }
    }

    const productForm = section.querySelector('product-form-component');
    if (productForm instanceof HTMLElement && productForm.dataset.productId) {
      section.dataset.productId = productForm.dataset.productId;
    }

    delete section.dataset.ctrPdpMainInit;
    initCtrPdpMain(section, { skipProductSelectorInit: true });

    // URL sync disabled for now — keep product selection client-side only.
    // if (productKey) {
    //   const analyticsUrl = new URL(window.location.href);
    //   analyticsUrl.searchParams.set('formula', productKey);
    //   history.replaceState({}, '', analyticsUrl);
    // }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return;
    console.warn('CTR PDP product switch failed', error);
  } finally {
    if (!abortController.signal.aborted) {
      loadingTargets.forEach((target) => {
        target.removeAttribute('aria-busy');
        target.classList.remove('is-loading');
      });
    }
  }
}

/**
 * @param {HTMLElement} section
 */
function initProductSwitcher(section) {
  const selectorRoot = section.querySelector('[data-ctr-pdp-product-selector]');
  if (!selectorRoot || selectorRoot.dataset.ctrPdpProductSelectorInit === 'true') return;

  selectorRoot.dataset.ctrPdpProductSelectorInit = 'true';

  let activeProductKey =
    selectorRoot.querySelector('input[type="radio"]:checked')?.dataset.productKey || 'bundle';

  selectorRoot.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    const productKey = input.dataset.productKey || '';
    if (!productKey || productKey === activeProductKey) return;

    activeProductKey = productKey;
    switchProductFormula(section, input);
  });

  // Deep-link formula restore disabled for now — no URL change on product selection.
  // const formulaParam = new URL(window.location.href).searchParams.get('formula');
  // if (formulaParam && formulaParam !== activeProductKey) {
  //   const targetInput = selectorRoot.querySelector(
  //     `input[type="radio"][data-product-key="${formulaParam}"]`
  //   );
  //   if (targetInput instanceof HTMLInputElement) {
  //     targetInput.checked = true;
  //     activeProductKey = formulaParam;
  //     switchProductFormula(section, targetInput);
  //   }
  // }
}

/**
 * @param {HTMLElement} section
 * @param {{ skipProductSelectorInit?: boolean }} [options]
 */
function initCtrPdpMain(section, options = {}) {
  if (!section || section.dataset.ctrPdpMainInit === 'true') return;
  section.dataset.ctrPdpMainInit = 'true';

  const productId = section.dataset.productId;

  // Gallery
  const gallery = section.querySelector('[data-product-buy-box-gallery]');
  if (gallery) {
    const stage = gallery.querySelector('.ctr-pdp-main__gallery-stage');
    const slides = Array.from(gallery.querySelectorAll('[data-product-buy-box-gallery-slide]'));
    const dots = Array.from(gallery.querySelectorAll('[data-product-buy-box-gallery-dot]'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-product-buy-box-gallery-thumb]'));
    const prev = gallery.querySelector('[data-product-buy-box-gallery-prev]');
    const next = gallery.querySelector('[data-product-buy-box-gallery-next]');
    let index = 0;

    if (slides.length) {
      slides.forEach((slide) => {
        const video = slide.querySelector('video');
        if (!video) return;
        video.addEventListener('play', () => slide.classList.add('is-playing'));
        video.addEventListener('pause', () => slide.classList.remove('is-playing'));
        video.addEventListener('ended', () => slide.classList.remove('is-playing'));
      });

      const show = (nextIndex) => {
        const previousIndex = index;
        index = (nextIndex + slides.length) % slides.length;
        if (previousIndex !== index) {
          const previousVideo = slides[previousIndex]?.querySelector('video');
          if (previousVideo && !previousVideo.paused) previousVideo.pause();
        }

        slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === index));
        dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
        thumbs.forEach((thumb, thumbIndex) => thumb.classList.toggle('is-active', thumbIndex === index));

        const activeSlide = slides[index];
        stage?.classList.toggle('is-video-active', activeSlide?.dataset.slideType === 'video');
        const activeVideo = activeSlide?.querySelector('video');
        if (activeVideo && activeSlide.dataset.autoplay === 'true') {
          const playAttempt = activeVideo.play();
          if (playAttempt && typeof playAttempt.catch === 'function') playAttempt.catch(() => {});
        }
      };

      dots.forEach((dot, dotIndex) => {
        dot.addEventListener('click', () => show(dotIndex));
      });

      thumbs.forEach((thumb, thumbIndex) => {
        thumb.addEventListener('click', () => show(thumbIndex));
      });

      prev?.addEventListener('click', () => show(index - 1));
      next?.addEventListener('click', () => show(index + 1));
      show(0);
    }
  }

  const variantDataScript = section.querySelector('[data-product-buy-box-variant-data]');
  const variantData = (() => {
    if (!variantDataScript?.textContent) return {};

    try {
      return JSON.parse(variantDataScript.textContent);
    } catch (error) {
      console.warn('CTR PDP main variant data could not be parsed', error);
      return {};
    }
  })();

  const selector = section.querySelector('[data-product-buy-box-variant-selector]');
  const comparePrice = section.querySelector('[data-product-buy-box-compare-price]');
  const saveLine = section.querySelector('[data-product-buy-box-save-line]');
  const microcopy = section.querySelector('[data-product-buy-box-microcopy]');
  const oneTimeLink = section.querySelector('[data-product-buy-box-one-time-link]');
  const productForms = Array.from(section.querySelectorAll('product-form-component'));
  let oneTimeRequestController = null;

  initSubscriptionFreeGiftForms(section);
  initSubscriptionFreeGiftCleanup();

  async function clearCart(clearUrl) {
    if (!clearUrl) return;

    await fetch(clearUrl, {
      method: 'POST',
      headers: { Accept: 'application/json' },
    });
  }

  function getSelectedOptions() {
    if (!selector) return [];

    const checkedOptions = Array.from(selector.querySelectorAll('input[type="radio"]:checked'));
    return checkedOptions
      .sort((a, b) => Number(a.dataset.optionPosition || 0) - Number(b.dataset.optionPosition || 0))
      .map((input) => input.dataset.optionValue || input.value);
  }

  function findVariantIdFromSelectedOptions() {
    const selectedOptions = getSelectedOptions();
    const selectedTitle = selectedOptions.join(' / ');
    const matchingVariant = Object.values(variantData).find((variant) => variant.title === selectedTitle);

    return (
      matchingVariant?.id ||
      selector?.querySelector('input[type="radio"]:checked[data-variant-id]')?.dataset.variantId
    );
  }

  function updateButton(button, variant) {
    if (!button) return;

    const isAvailable = Boolean(variant?.available);
    button.disabled = !isAvailable;

    const label = isAvailable ? button.dataset.availableLabel : button.dataset.soldOutLabel;
    const text = button.querySelector('[data-product-buy-box-atc-text]');
    if (text && label) text.textContent = label;
  }

  function updateBioniqUnitsInput(input, variant) {
    if (!input) return;

    if (variant?.bioniqUnits == null || variant.bioniqUnits === '') {
      input.value = '';
      input.disabled = true;
      return;
    }

    input.value = String(variant.bioniqUnits);
    input.disabled = false;
  }

  function updateSellingPlanInput(input, variant) {
    if (!input) return;

    if (!variant?.sellingPlanId) {
      input.value = '';
      input.disabled = true;
      return;
    }

    input.value = String(variant.sellingPlanId);
    input.disabled = false;
  }

  function updatePriceElements(variant) {
    const priceTargets = section.querySelectorAll('[data-product-buy-box-price]');
    priceTargets.forEach((el) => {
      el.textContent = variant.displayPrice || '';
    });

    const compareTargets = section.querySelectorAll('[data-product-buy-box-compare-price]');
    compareTargets.forEach((el) => {
      el.textContent = variant.displayCompareAtPrice || '';
      el.hidden = !variant.displayCompareAtPrice;
    });
  }

  function updateVariant(variantId) {
    const variant = variantData[String(variantId)];
    if (!variant) return;

    productForms.forEach((form) => {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) variantInput.value = variant.id;

      updateButton(form.querySelector('[data-product-buy-box-atc]'), variant);
      updateBioniqUnitsInput(form.querySelector('[data-product-buy-box-bioniq-units-property]'), variant);
      updateSellingPlanInput(form.querySelector('[data-product-buy-box-selling-plan]'), variant);
    });

    updatePriceElements(variant);
    if (saveLine) saveLine.textContent = variant.saveLine || '';
    if (microcopy) microcopy.textContent = variant.microcopy || '';
    if (oneTimeLink) {
      oneTimeLink.href = variant.oneTimeUrl;
      oneTimeLink.textContent = (oneTimeLink.dataset.labelTemplate || '').replace(
        '[price]',
        variant.oneTimePrice || ''
      );
    }
  }

  selector?.addEventListener('change', () => {
    const variantId = findVariantIdFromSelectedOptions();
    if (variantId) updateVariant(variantId);
  });

  function getCartSectionIds() {
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    });
    return Array.from(new Set(sectionIds));
  }

  function openOneTimeCartDrawer(data) {
    const nativeCartDrawer = getNativeCartDrawer();
    const nativeCartDialog = getNativeCartDialog();

    if (nativeCartDrawer) {
      nativeCartDrawer.open?.();
      return;
    }

    if (nativeCartDialog) {
      nativeCartDialog.showModal();
      return;
    }

    const rebuy = window.Rebuy;
    const smartCart = rebuy?.SmartCart || rebuy?.Smartcart || rebuy?.smartCart;

    rebuy?.Cart?.fetchCart?.();
    smartCart?.render?.();
    smartCart?.show?.();
    smartCart?.open?.();

    document.dispatchEvent(
      new CustomEvent('rebuy:cart.change', {
        bubbles: true,
        detail: { cart: data, source: 'ctr-pdp-main-one-time' },
      })
    );

    window.setTimeout(() => {
      smartCart?.show?.();
      smartCart?.open?.();
    }, 50);
  }

  function getNativeCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    return typeof cartDrawer?.open === 'function' ? cartDrawer : null;
  }

  function getNativeCartDialog() {
    const dialog = document.querySelector('cart-drawer-component dialog');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  function dispatchOneTimeCartUpdate(data, variant) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: data,
          sourceId: section.dataset.sectionId || 'ctr-pdp-main-one-time',
          data: {
            source: 'product-form-component',
            itemCount: 1,
            productId,
            variantId: variant?.id ? String(variant.id) : undefined,
            sections: data.sections || {},
          },
        },
      })
    );

    openOneTimeCartDrawer(data);
  }

  oneTimeLink?.addEventListener(
    'click',
    async (event) => {
      if (oneTimeLink.dataset.oneTimeBehavior === 'checkout') {
        if (oneTimeLink.dataset.clearCartBeforeCheckout !== 'true') return;

        event.preventDefault();
        event.stopPropagation();

        oneTimeLink.setAttribute('aria-busy', 'true');
        try {
          await clearCart(oneTimeLink.dataset.cartClearUrl);
        } finally {
          window.location.href = oneTimeLink.href;
        }
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const variantId = findVariantIdFromSelectedOptions();
      const variant = variantData[String(variantId)];
      if (!variant?.available) return;

      oneTimeRequestController?.abort();
      oneTimeRequestController = new AbortController();
      oneTimeLink.setAttribute('aria-busy', 'true');

      const sectionIds = getCartSectionIds();
      const oneTimeItem = { id: Number(variant.id), quantity: 1 };
      const preorderPropertyName = oneTimeLink.dataset.propertyName || '';
      if (preorderPropertyName) {
        oneTimeItem.properties = { [preorderPropertyName]: oneTimeLink.dataset.propertyValue || '' };
      }
      const body = JSON.stringify({
        items: [oneTimeItem],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      });

      try {
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body,
          signal: oneTimeRequestController.signal,
        });
        const data = await response.json();

        if (!response.ok || data.status || data.errors) {
          window.location.href = variant.oneTimeUrl || oneTimeLink.href;
          return;
        }

        dispatchOneTimeCartUpdate(data, variant);
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.href = variant.oneTimeUrl || oneTimeLink.href;
      } finally {
        oneTimeLink.removeAttribute('aria-busy');
      }
    },
    { capture: true }
  );

  initFactsPopups(section);
  initOfferCalloutModal(section);

  if (!options.skipProductSelectorInit) {
    initProductSwitcher(section);
  }
}

document.querySelectorAll('.ctr-pdp-main[data-section-id]').forEach(initCtrPdpMain);

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target?.querySelector?.('.ctr-pdp-main[data-section-id]') || event.target;
  if (section?.classList?.contains('ctr-pdp-main')) {
    delete section.dataset.ctrPdpMainInit;
    const selector = section.querySelector('[data-ctr-pdp-product-selector]');
    if (selector) delete selector.dataset.ctrPdpProductSelectorInit;
    initCtrPdpMain(section);
  }
});
